import { ChangeEvent, useMemo, useState } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { usePageContext } from "@ryde/redux";
import {
  BulkRecalculatePriceSelectionScope,
  selectedRidesSelector,
  selectedTableRowsIdsAtomFamily,
} from "@ryde/shared";

import {
  BulkRecalculateRateType,
  IBulkRecalculateSelectionResult,
} from "./bulk-recalculate-selection.models";
import {
  getBulkRecalculateSelectedRideIds,
  getBulkRecalculateSelectionCounts,
  getBulkRecalculateSwitchCounts,
} from "./bulk-recalculate-selection.utils";

export const useBulkRecalculateSelection = (
  rateType: BulkRecalculateRateType = BulkRecalculateRateType.PriceRate,
  isAdvancedSelectionEnabled = false,
): IBulkRecalculateSelectionResult => {
  const { entity } = usePageContext();
  const selectedRideIds = useRecoilValue(
    selectedTableRowsIdsAtomFamily(entity),
  );
  const selectedRides = useRecoilValue(selectedRidesSelector);
  const [selectionScope, setSelectionScope] = useState(
    BulkRecalculatePriceSelectionScope.All,
  );

  const selectionCounts = useMemo(
    () => getBulkRecalculateSelectionCounts(selectedRides, rateType),
    [rateType, selectedRides],
  );

  const selectedRideIdsForRecalculate = useMemo(() => {
    if (!isAdvancedSelectionEnabled) {
      return selectedRideIds;
    }

    return getBulkRecalculateSelectedRideIds(
      selectionScope,
      selectionCounts,
      selectedRideIds,
    );
  }, [
    isAdvancedSelectionEnabled,
    selectionCounts,
    selectionScope,
    selectedRideIds,
  ]);

  const switchCounts = useMemo(() => {
    const ridesInScope = isAdvancedSelectionEnabled
      ? selectedRides.filter(ride =>
          selectedRideIdsForRecalculate.includes(ride.id),
        )
      : selectedRides;

    return getBulkRecalculateSwitchCounts(ridesInScope, rateType);
  }, [
    isAdvancedSelectionEnabled,
    rateType,
    selectedRideIdsForRecalculate,
    selectedRides,
  ]);

  const onSelectionScopeChange = useEvent(
    (event: ChangeEvent<HTMLInputElement>): void => {
      setSelectionScope(
        event.target.value as BulkRecalculatePriceSelectionScope,
      );
    },
  );

  return {
    selectionScope,
    onSelectionScopeChange,
    selectionCounts,
    switchCounts,
    selectedRideIdsForRecalculate,
  };
};
